/**
 * The S4 TIER TABLE — which part of a specialist's workspace may be edited,
 * which is read-only, and which is never served at all.
 *
 * Normative source: the `lexiun-agent-specialists` handoff §3 (S4 row) as
 * CORRECTED by the spec authority 2026-08-21, and `specialist-home-guide.md` §1 —
 *
 *     kb/         EDIT (OKF-checked on save)
 *     memory/     READ-ONLY      curator-writes-only; no operator affordance, ever
 *     wiki/       READ-ONLY      (reserved, not built — a missing subdir is legitimate)
 *     _reserved/  NEVER SERVED   ⚖ DS-P, ABOVE the mount boundary
 *     staging/    NEVER SERVED   it only exists as `_reserved/staging/`
 *     .git/       NEVER SERVED   history + forward-apply revert are the only doors
 *
 * ⚠ `staging` AND `_reserved` WERE PINNED AS READ-ONLY TIERS HERE AND THAT WAS
 * WRONG — caught by the spec authority within the hour, on the confirmation this
 * table was pinned early to invite. They are not tiers this surface may list
 * without an edit button; they are **not part of this surface at all**. The S4
 * tree is rooted at `shared/sp-<id>/`, and `_reserved/` (which is where
 * `staging/` lives) sits ABOVE `shared/` — the ⚖ DS-P area never mounted
 * anywhere. Calling them read-only would invite a later change to LIST a
 * never-mounted region: a disclosure, not a missing button.
 *
 * ⚠ AND THE MECHANISM IS OFF BY ONE FROM HOW IT IS USUALLY DESCRIBED — measured
 * live 2026-08-21, not assumed. Workspace-relative `_reserved`, `staging`,
 * `_reserved/staging` and `_reserved/settings.json` all answer **`NOT_FOUND`
 * (404)**: they are not inside the workspace, so there is nothing there to
 * forbid. `PATH_ESCAPE` (403) is what an UPWARD traversal earns — `../_reserved`,
 * `../../_reserved`.
 *
 * ⚠⚠ AND A CORRECTION TO THE CORRECTION, WHICH IS THE PART WORTH READING. From
 * those measurements this file first concluded that `FORBIDDEN_PATH` "can never
 * fire on this layout" — because `.git` 404s. **That was wrong, and lexserver
 * caught it.** `.index` (the generated FTS snapshot) exists INSIDE every bundle
 * and answers `FORBIDDEN_PATH` (403), verified live here:
 * `'.index' is inside a path this API never serves (.git, .index)`. The error
 * was not the measurement — `.git` really does 404 — it was generalising from
 * ONE path to a whole class, and the conclusion would have justified deleting a
 * live branch. Two guards, two layouts: something ABOVE the workspace is refused
 * by CONTAINMENT; a hidden dir INSIDE one is refused by `HIDDEN_DIRS`.
 *
 * WHY THIS IS A FUNCTION AND NOT A COMMENT: the write half (lexserver slice 4)
 * does not exist yet, so today every path is read-only by simple absence of a
 * write call. That is the state in which a tier rule is easy to get wrong and
 * impossible to notice — the affordance appears later, in a different session,
 * next to a table that was never machine-checked. Pinning it now means slice 4
 * lands INTO a rule instead of authoring one.
 *
 * ⚠ `editable` here is the TIER's verdict, not permission to render a button.
 * The button also needs `WORKSPACE_WRITE_AVAILABLE`, which is false until the
 * write API ships. Two independent gates: a tier mistake cannot by itself put
 * an edit control on `memory/`, and shipping the write API cannot by itself put
 * one anywhere the tier forbids.
 */

/** Flip to `true` only when lexserver's kb-write route is LIVE (slice 4). */
export const WORKSPACE_WRITE_AVAILABLE = false;

export type WorkspaceTier =
  /** Curated domain knowledge. The only editable tier. */
  | "kb"
  /** The specialist's own accumulated memory. Read-only to operators. */
  | "memory"
  /** The curator's queue. Viewable; its verbs belong to S5, not the editor. */
  | "staging"
  /** Reference wiki. Read-only. */
  | "wiki"
  /** `_reserved/` — ⚖ DS-P, off the mount boundary. Its knob has its own surface. */
  | "reserved"
  /** Never served raw at any depth: `.git`, `.index`. */
  | "hidden"
  /** Anything else the tree returns. Read-only until someone rules on it. */
  | "other";

export interface TierVerdict {
  tier: WorkspaceTier;
  /** The tier's own rule. NOT sufficient to render an edit control. */
  editable: boolean;
  /** True when this client must not even REQUEST the path. */
  neverServed: boolean;
  /** Operator-facing sentence for why this path is not editable. */
  note: string;
}

/** Names never traversed AT ANY DEPTH. Mirrors lexserver's `HIDDEN_DIRS` — kept
 *  as a client-side refusal too, so a typed or restored path never becomes a
 *  request the server has to refuse. */
const HIDDEN_SEGMENTS = new Set([".git", ".index"]);


/**
 * Keyed by the leading PATH SEGMENT — which makes the `_reserved`/`staging`
 * never-served rule ROOT-SCOPED, and that is load-bearing rather than
 * incidental. Those names identify regions OUTSIDE the workspace, not forbidden
 * words: a directory `kb/staging/` is ordinary curated content and IS served, so
 * refusing it would hide real kb material behind a rule about somewhere else.
 * `.git`/`.index` are the opposite case, checked per-segment above because they
 * are refused wherever they appear.
 *
 * Keyed by the leading PATH SEGMENT, which is not always the tier's name —
 *  `_reserved/` carries an underscore on disk (⚖ DS-P) and `reserved` in the
 *  vocabulary. Keying this by the tier name instead silently classified the
 *  real path as `other`, which is read-only and therefore looked fine. */
const TIERS: Record<string, TierVerdict> = {
  kb: {
    tier: "kb",
    editable: true,
    neverServed: false,
    note: "Curated domain knowledge — editable, OKF-checked on save.",
  },
  memory: {
    tier: "memory",
    editable: false,
    neverServed: false,
    note: "The specialist's own accumulated memory — read-only to operators; the specialist writes it.",
  },
  staging: {
    tier: "staging",
    editable: false,
    neverServed: true,
    note: "Not part of this surface — the curation queue exists only as `_reserved/staging/`, above the mount boundary. The curation surface reaches it through its own API, never this tree.",
  },
  wiki: {
    tier: "wiki",
    editable: false,
    neverServed: false,
    note: "Reference wiki — read-only.",
  },
  _reserved: {
    tier: "reserved",
    editable: false,
    neverServed: true,
    note: "Not part of this surface — the ⚖ DS-P area sits ABOVE the mount boundary and is never mounted anywhere. The apply-mode knob is reached by its own endpoint, not by walking this tree.",
  },
};

/**
 * Classify a workspace-relative path.
 *
 * The workspace ROOT (`""`) is `other`/read-only: a root is not a tier, and
 * defaulting it to anything editable would make the most-reachable path in the
 * tree the most permissive one.
 */
export function tierFor(relPath: string): TierVerdict {
  const clean = relPath.replace(/^\/+/, "");
  const segments = clean.split("/").filter(Boolean);

  if (segments.some((s) => HIDDEN_SEGMENTS.has(s))) {
    return {
      tier: "hidden",
      editable: false,
      neverServed: true,
      note: "Never served raw — per-file history and forward-apply revert are the only doors into git.",
    };
  }

  const head = segments[0];
  if (head) {
    // Root segment only — see the TIERS docblock: `kb/staging/` is real content.
    const t = TIERS[head];
    if (t) return t;
  }

  return {
    tier: "other",
    editable: false,
    neverServed: false,
    note: "Outside the tiers the specialists spec names — read-only until it is ruled on.",
  };
}

/**
 * The ONLY predicate a component may use to decide whether to render an edit
 * control. Both gates, in one place, so neither can be forgotten separately.
 *
 * ⚠ `writeAvailable` IS A PARAMETER FOR A REASON, and not a testing seam bolted
 * on for convenience. While the constant is `false` this function returns
 * `false` for every input, so NO test of it can tell "checks the tier" apart
 * from "ignores the tier" — the availability gate short-circuits the tier gate
 * out of observability entirely. That was measured, not theorised: a regression
 * deleting `tierFor(relPath).editable` from this expression passed the whole
 * suite. A rule nothing can falsify is not a guarded rule, so the second gate
 * is made injectable and the tier half is proven against `writeAvailable:true`.
 * Production call sites pass one argument and are unaffected.
 */
export function mayEdit(relPath: string, writeAvailable = WORKSPACE_WRITE_AVAILABLE): boolean {
  return writeAvailable && tierFor(relPath).editable;
}
